import React, { useContext, useState } from 'react';
import * as css from './godtabetingelser.module.scss';
import translations from './godtabetingelser.i18n';
import aboutTranslations from '../login/about.i18n';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import { PrivacyPolicy } from './rightsandprivacy';
import { StudentUserContext } from '../user/StudentUserStore';
import StudentIcon from 'url:./icons/student_active.svg';
import PraksisstedIcon from 'url:./icons/praksissted_active.svg';
import StudiestedIcon from 'url:./icons/studiested_active.svg';
import { PsaUserContext } from '../user/PsaUserStore';
import { UhaUserContext } from '../user/UhaUserStore';
import { StudentApiContext } from '../api/StudentApiContext';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useSetDocumentTitle } from '../api/useSetDocumentTitle';
import { useNavigate } from 'react-router-dom';
import {
  CustomizableDialogButtonGroup,
  CustomizableDialogContainer,
  CustomizableDialogContent,
  CustomizableDialogFooter,
} from '../components/dialog';
import Checkbox from '../components/input/checkbox';
import { useFormatMessageFor } from '../i18n/translation';

const lastPageNum = 3;

export enum UserType {
  Student = 'Student',
  Studiested = 'Studiested',
  Praksissted = 'Praksissted',
}

const useLabels = () => useFormatMessageFor(translations);

interface CancelBtnProps {
  onCancel: () => void;
}

const CancelBtn = ({ onCancel }: CancelBtnProps) => {
  const labels = useLabels();

  return (
    <Button.Quaternary onClick={onCancel}>{labels.cancel}</Button.Quaternary>
  );
};

interface GodtaBetingelserProps {
  userType: UserType;
}
const GodtaBetingelser: React.FC<
  React.PropsWithChildren<GodtaBetingelserProps>
> = ({ userType }) => {
  const [pageNum, setPageNum] = useState(1);
  useSetDocumentTitle('Betingelser - Arbeidslivsportalen', []);

  const nextModal = () => {
    setPageNum(pageNum + 1);
  };

  const onCancel = () => {
    document.location.href = '/login/logout';
  };

  return (
    <CustomizableDialogContainer
      isOpen
      onCancel={onCancel}
      className={css.modal}
    >
      <CustomizableDialogContent className={css.content}>
        {pageNum === 1 && <MainContentPage1 />}
        {pageNum === 2 && <MainContentPage2 userType={userType} />}
        {pageNum === 3 && <MainContentPage3 />}
        <div className={css.progress}>
          <span aria-current="step">{pageNum}</span> / 3
        </div>
      </CustomizableDialogContent>
      {pageNum === lastPageNum ? (
        <ButtonRowWithGodta onCancel={onCancel} userType={userType} />
      ) : (
        <ButtonRowPage1or2 onCancel={onCancel} nextModal={nextModal} />
      )}
    </CustomizableDialogContainer>
  );
};

interface PageProps {
  userType: UserType;
}

const MainContentPage1 = () => {
  const labels = useLabels();

  return (
    <>
      <h1>{labels.page1Title}</h1>
      <p>{labels.page1Description}</p>
    </>
  );
};

const MainContentPage2: React.FC<React.PropsWithChildren<PageProps>> = ({
  userType,
}) => {
  const { formatMessage } = useIntl();
  const icon =
    userType === UserType.Student
      ? StudentIcon
      : userType === UserType.Studiested
      ? StudiestedIcon
      : PraksisstedIcon;
  const leadText = formatMessage({
    id: aboutTranslations[`${userType.toLowerCase()}InfoSubtitle`].id,
  });
  const mainText = formatMessage({
    id: aboutTranslations[`${userType.toLowerCase()}InfoMainText`].id,
  });
  const title = formatMessage({ id: translations[`page2Title${userType}`].id });

  return (
    <>
      <h1>{title}</h1>
      <img src={icon} alt="" />
      <p>{leadText}</p>
      <p dangerouslySetInnerHTML={{ __html: mainText }} />
    </>
  );
};

const MainContentPage3 = () => (
  <div className={css.policy}>
    <PrivacyPolicy />
  </div>
);

interface ButtonRowCommonProps {
  onCancel: () => void;
}

interface ButtonRowPage1or2Props extends ButtonRowCommonProps {
  nextModal: () => void;
}

const ButtonRowPage1or2 = ({ onCancel, nextModal }: ButtonRowPage1or2Props) => {
  const labels = useLabels();

  return (
    <CustomizableDialogFooter>
      <CustomizableDialogButtonGroup>
        <CancelBtn onCancel={onCancel} />
        <Button.Secondary onClick={nextModal}>{labels.neste}</Button.Secondary>
      </CustomizableDialogButtonGroup>
    </CustomizableDialogFooter>
  );
};

interface ButtonRowWithGodtaProps extends ButtonRowCommonProps {
  userType: UserType;
}

const ButtonRowWithGodta = ({
  userType,
  onCancel,
}: ButtonRowWithGodtaProps) => {
  const labels = useLabels();
  const navigate = useNavigate();

  const [harGodtattBetingelser, setHarGodtattBetingelser] = useState(false);

  const { brukerApi: studentBrukerApi } = useContext(StudentApiContext);
  const { brukerApi: studiestedBrukerApi } = useContext(StudiestedApiContext);
  const { brukerApi: praksisstedBrukerApi } = useContext(PraksisstedApiContext);
  const { setStudentUser, studentUser } = useContext(StudentUserContext);
  const { setUhaUser, uhaUser } = useContext(UhaUserContext);
  const { setPsaUser, psaUser } = useContext(PsaUserContext);

  const godtaBetingelser = (userType: UserType) => {
    if (userType === UserType.Studiested) {
      return godtaBetingelserStudiested();
    }
    if (userType === UserType.Student) {
      return godtaBetingelserStudent();
    }
    godtaBetingelserPraksissted();
  };

  const godtaBetingelserStudiested = () => {
    studiestedBrukerApi
      .brukerinfoInnstillingerPut({
        brukerinnstillingerDto: {
          harGodtattBetingelser: true,
        },
      })
      .then(() => {
        uhaUser!.innstillinger.harGodtattBetingelser = true;
        setUhaUser({ ...uhaUser! });
        navigate('/', { replace: true });
      });
  };

  const godtaBetingelserStudent = () => {
    studentBrukerApi
      .brukerinfoInnstillingerPut({
        brukerinnstillingerDto: {
          harGodtattBetingelser: true,
        },
      })
      .then(() => {
        studentUser!.innstillinger.harGodtattBetingelser = true;
        setStudentUser({ ...studentUser! });
        navigate('/', { replace: true });
      });
  };

  const godtaBetingelserPraksissted = () => {
    praksisstedBrukerApi
      .brukerinfoInnstillingerPut({
        brukerinnstillingerDto: {
          harGodtattBetingelser: true,
        },
      })
      .then(() => {
        psaUser!.innstillinger.harGodtattBetingelser = true;
        setPsaUser({ ...psaUser! });
        navigate('/', { replace: true });
      });
  };

  return (
    <CustomizableDialogFooter className={css.footer}>
      <Checkbox
        id="godtatt"
        checked={harGodtattBetingelser}
        setChecked={setHarGodtattBetingelser}
        label={labels.jegGodkjenner}
      />
      <CustomizableDialogButtonGroup className={css.buttonContainer}>
        <CancelBtn onCancel={onCancel} />
        <Button.Secondary
          disabled={!harGodtattBetingelser}
          onClick={() => godtaBetingelser(userType)}
        >
          {labels.godkjenn}
        </Button.Secondary>
      </CustomizableDialogButtonGroup>
    </CustomizableDialogFooter>
  );
};

export default GodtaBetingelser;
